import { useState, useMemo } from 'react'
import { colors, fonts } from './UI'

const PREFS_KEY = 'tic_notification_prefs'
const DISMISSED_KEY = 'tic_notifications_dismissed'


const SEEN_KEY = 'tic_notification_seen'

function getSeenAt() {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY) || '{}') } catch { return {} }
}

function recordSeen(ids) {
  try {
    const seen = getSeenAt()
    const now = Date.now()
    ids.forEach(id => { if (!seen[id]) seen[id] = now })
    localStorage.setItem(SEEN_KEY, JSON.stringify(seen))
  } catch {}
}

function relativeLabel(id) {
  const seen = getSeenAt()
  const ts = seen[id]
  if (!ts) return 'today'
  const days = Math.floor((Date.now() - ts) / 86400000)
  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'
  return `${days} days ago`
}

function getPrefs() {
  try {
    const s = localStorage.getItem(PREFS_KEY)
    const defaults = { billsDueSoon: true, paydayReminder: true, lowBalance: true, goalMilestone: true }
    return s ? { ...defaults, ...JSON.parse(s) } : defaults
  } catch { return { billsDueSoon: true, paydayReminder: true, lowBalance: true, goalMilestone: true } }
}

function getDismissed() {
  try { return JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]') } catch { return [] }
}

function saveDismissed(ids) {
  try { localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids.slice(-100))) } catch {}
}

export function generateNotifications({ bills, goals, stsValue, cycleStart, getUpcoming }) {
  const prefs = getPrefs()
  const dismissed = getDismissed()
  const notifications = []

  const today = new Date()
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  // Bills due soon
  if (prefs.billsDueSoon) {
    const upcoming = getUpcoming ? getUpcoming() : []
    upcoming.filter(b => b.daysUntil >= 0 && b.daysUntil <= 7).forEach(b => {
      const id = `bill-${b.id}-${b.daysUntil <= 1 ? 'today' : b.daysUntil}`
      if (!dismissed.includes(id)) {
        const dueDate = ''
        notifications.push({
          id, type: 'bill',
          emoji: '📅',
          title: `${b.name} due ${b.daysUntil === 0 ? 'today' : b.daysUntil === 1 ? 'tomorrow' : `in ${b.daysUntil} days`}`,
          body: `£${b.amount?.toFixed(2)} ${b.daysUntil <= 1 ? '- make sure funds are ready' : '- coming up soon'}`,
          urgent: b.daysUntil <= 1,
        })
      }
    })
  }

  // Payday reminder - first 2 days of cycle
  if (prefs.paydayReminder && cycleStart) {
    const cycleStartDate = new Date(cycleStart)
    const daysSinceCycleStart = Math.floor((startOfToday - cycleStartDate) / 86400000)
    if (daysSinceCycleStart <= 1) {
      const id = `payday-${cycleStart}`
      if (!dismissed.includes(id)) {
        notifications.push({
          id, type: 'payday',
          emoji: '💰',
          title: "It's payday!",
          body: 'Update your account balances so your Safe to Spend is accurate.',
          urgent: false,
        })
      }
    }
  }

  // Low STS
  if (prefs.lowBalance && stsValue !== null && stsValue < 100) {
    const id = `low-sts-${startOfToday.toISOString().slice(0, 10)}`
    if (!dismissed.includes(id)) {
      notifications.push({
        id, type: 'low',
        emoji: '⚠️',
        title: 'Safe to Spend is low',
        body: `You have £${Math.max(0, Math.round(stsValue)).toLocaleString()} left before payday. Watch your spending.`,
        urgent: true,
      })
    }
  }

  // Goal milestones
  if (prefs.goalMilestone) {
    goals.filter(g => g.type === 'savings' && g.target_amount > 0).forEach(g => {
      const pct = Math.round((g.current_amount / g.target_amount) * 100)
      const milestones = [25, 50, 75, 100]
      milestones.forEach(m => {
        if (pct >= m) {
          const id = `goal-${g.id}-${m}`
          if (!dismissed.includes(id)) {
            notifications.push({
              id, type: 'goal',
              emoji: m === 100 ? '🏆' : '🎯',
              title: m === 100 ? `${g.name} complete!` : `${m}% of ${g.name}`,
              body: m === 100 ? `You've reached your £${g.target_amount?.toLocaleString()} goal. Amazing work!` : `You've saved £${g.current_amount?.toLocaleString()} of £${g.target_amount?.toLocaleString()}.`,
              urgent: m === 100,
                })
          }
        }
      })
    })
  }

  // Record first-seen timestamp for each notification
  recordSeen(notifications.map(n => n.id))

  // Attach relative label based on when first seen
  return notifications.map(n => ({ ...n, date: relativeLabel(n.id) }))
}

export default function NotificationPanel({ notifications, onClose, onDismiss }) {
  const urgentCount = notifications.filter(n => n.urgent).length
  const isDesktop = window.innerWidth >= 768

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', display: 'flex', flexDirection: 'column', justifyContent: isDesktop ? 'center' : 'flex-end', alignItems: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: colors.cardMid, borderRadius: isDesktop ? '16px' : '24px 24px 0 0', padding: isDesktop ? '0 0 24px' : '0 0 max(32px, env(safe-area-inset-bottom))', maxHeight: '80vh', display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '480px' }}>

        {/* Handle - mobile only */}
        {!isDesktop && <div style={{ width: '36px', height: '4px', background: 'rgba(255,255,255,0.15)', borderRadius: '2px', margin: '10px auto 0' }} />}

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontFamily: fonts.heading, fontSize: '17px', fontWeight: 700, color: colors.text }}>Notifications</span>
            {notifications.length > 0 && (
              <span style={{ background: colors.gold, color: colors.onAccent, fontSize: '12px', fontWeight: 700, borderRadius: '10px', padding: '2px 8px' }}>{notifications.length}</span>
            )}
          </div>
          {notifications.length > 0 && (
            <button onClick={() => {
              const dismissed = getDismissed()
              const newDismissed = [...dismissed, ...notifications.map(n => n.id)]
              saveDismissed(newDismissed)
              notifications.forEach(n => onDismiss(n.id))
            }} style={{ background: 'none', border: 'none', fontSize: '13px', fontWeight: 600, color: colors.muted, cursor: 'pointer', fontFamily: fonts.body }}>
              Clear all
            </button>
          )}
        </div>

        {/* List */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {notifications.length === 0 ? (
            <div style={{ padding: '48px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>✅</div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: colors.text, marginBottom: '6px' }}>All caught up</div>
              <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted }}>No notifications right now.</div>
            </div>
          ) : notifications.map((n, i) => (
            <div key={n.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 20px', borderBottom: i < notifications.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: n.urgent ? 'rgba(224,82,82,0.12)' : 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>
                {n.emoji}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: n.urgent ? colors.red : colors.text }}>{n.title}</div>
                  {n.date && <div style={{ fontSize: '12px', fontWeight: 600, color: `rgba(${colors.textRgb},0.35)`, flexShrink: 0, marginLeft: '8px' }}>{n.date}</div>}
                </div>
                <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, lineHeight: 1.5 }}>{n.body}</div>
              </div>
              <button onClick={() => {
                const dismissed = getDismissed()
                saveDismissed([...dismissed, n.id])
                onDismiss(n.id)
              }} style={{ background: 'none', border: 'none', color: `rgba(${colors.textRgb},0.25)`, cursor: 'pointer', fontSize: '18px', padding: '4px', flexShrink: 0, lineHeight: 1 }}>×</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
